/**
 * Enrich payroll employees with the same salary-structure breakdown
 * used on Employee Profile (findApplicable + buildEmployeeStructureBreakdown).
 * Does not alter the calculation engine — display reuse only.
 */
import { findApplicableSalaryStructure } from "./salaryStructureMatch";
import { buildEmployeeStructureBreakdown } from "./employeeStructureBreakdown";
import { templateToStructure } from "../components/Admin/employee-management/salary-structure/utils/salaryStructurePersistence";
import {
  normalizeSalaryStructureMethod,
  resolveStructureMode,
} from "./salaryStructureMethod";

function monthKey(year, monthIndex) {
  const m = Number(monthIndex) + 1;
  return `${year}-${String(m).padStart(2, "0")}`;
}

function isBenefitOn(entry) {
  if (entry && typeof entry === "object") return entry.enabled === true;
  return !!entry;
}

/** Merge effective-dated statutory flags + benefits from payroll resolve-batch. */
function employeeWithStatutoryOverride(employee, override) {
  if (!override) return employee;
  const { benefits, source, ...flags } = override;
  const emp = { ...employee, ...flags };
  if (!benefits || typeof benefits !== "object") return emp;

  const pb = benefits.performanceBonus;
  if (pb && typeof pb === "object") {
    if (!isBenefitOn(pb)) {
      emp.performanceBonus = null;
    } else {
      const amt = Number(pb.amount ?? pb.performanceBonusAmount);
      if (Number.isFinite(amt) && amt > 0) emp.performanceBonus = amt;
      if (pb.period || pb.performanceBonusPeriod) {
        emp.performanceBonusPeriod = pb.period || pb.performanceBonusPeriod;
      }
      if (pb.frequency || pb.performanceBonusFrequency) {
        emp.performanceBonusFrequency = pb.frequency || pb.performanceBonusFrequency;
      }
    }
  }

  emp.statutoryBenefits = benefits;

  if (benefits.employerNps) {
    emp.employeeNps = {
      ...(typeof benefits.employerNps === "object" ? benefits.employerNps : {}),
      enabled: isBenefitOn(benefits.employerNps),
    };
  }
  if (benefits.mealVoucher) {
    emp.employeeMealVoucher = {
      ...(typeof benefits.mealVoucher === "object" ? benefits.mealVoucher : {}),
      enabled: isBenefitOn(benefits.mealVoucher),
    };
    emp.employeeMeal = emp.employeeMealVoucher;
  }

  return emp;
}

function collectNames(lists) {
  const names = [];
  const seen = new Set();
  (lists || []).forEach((list) => {
    (list || []).forEach((row) => {
      const name = String(row?.name || "").trim();
      if (!name || seen.has(name.toLowerCase())) return;
      seen.add(name.toLowerCase());
      names.push(name);
    });
  });
  return names;
}

/**
 * Stable component column order from structure templates (earnings → reimbursements →
 * common statutory employer → statutory deductions).
 */
export function buildPayrollBreakdownColumnKeys(structures = []) {
  const earningNames = [];
  const reimbNames = [];
  const earnSeen = new Set();
  const reimbSeen = new Set();

  (structures || []).forEach((structure) => {
    (structure.components || []).forEach((c) => {
      const name = String(c?.name || "").trim();
      if (!name) return;
      const key = name.toLowerCase();
      const cats = c.categories || {};
      const cat = String(c.category || "").toUpperCase();
      const isReimb =
        !!cats.reimbursement || cat === "REIMBURSEMENT";
      const isEarn =
        !!cats.earning ||
        cat === "EARNING" ||
        (!cats.deduction && !cats.benefit && !isReimb && !cat);
      if (isReimb) {
        if (!reimbSeen.has(key)) {
          reimbSeen.add(key);
          reimbNames.push(name);
        }
      } else if (isEarn || cats.benefit || cat === "BENEFIT" || cat === "EMPLOYER_BENEFIT") {
        if (!earnSeen.has(key)) {
          earnSeen.add(key);
          earningNames.push(name);
        }
      }
    });
  });

  return [
    ...earningNames,
    ...reimbNames,
    "PF (Employer)",
    "ESI (Employer)",
    "Gratuity",
    "PF (Employee)",
    "ESI (Employee)",
    "Professional Tax (PT)",
  ];
}

/**
 * @returns {Map<number|string, {
 *   structureName: string,
 *   structureId: number|null,
 *   amountsByName: Record<string, number>,
 *   monthlyCtc: number|null,
 *   grossPay: number|null,
 *   netPay: number|null,
 * }>}
 */
export function enrichEmployeesWithSalaryBreakdown({
  employees = [],
  structures = [],
  statutoryConfigJson = null,
  branchesList = [],
  selectedYear,
  selectedMonth,
  orgSalaryMethod = "CTC",
  statutoryOverridesByEmployeeId = null,
}) {
  const map = new Map();
  const effectiveMonth = monthKey(selectedYear, selectedMonth);
  const method = normalizeSalaryStructureMethod(orgSalaryMethod);

  (employees || []).forEach((employee) => {
    const id = employee?.id;
    if (id == null) return;

    const override =
      statutoryOverridesByEmployeeId instanceof Map
        ? statutoryOverridesByEmployeeId.get(id) ||
          statutoryOverridesByEmployeeId.get(String(id))
        : statutoryOverridesByEmployeeId?.[id] ||
          statutoryOverridesByEmployeeId?.[String(id)];
    const empForCalc = employeeWithStatutoryOverride(employee, override);

    const structure = findApplicableSalaryStructure(empForCalc, structures);
    const annual = Number(empForCalc?.ctc || empForCalc?.ctcAmount || 0);
    if (!structure || !annual) {
      map.set(id, {
        structureName: structure?.name || "—",
        structureId: structure?.id ?? null,
        amountsByName: {},
        monthlyCtc: annual ? Math.round(annual / 12) : null,
        grossPay: null,
        netPay: null,
      });
      return;
    }

    const mode = resolveStructureMode(
      method,
      structure.salaryType === "gross" ? "GROSS" : "CTC"
    );
    const salaryType = mode === "GROSS" ? "gross" : "ctc";

    try {
      const breakdown = buildEmployeeStructureBreakdown({
        structure,
        annualAmount: annual,
        salaryType,
        employee: empForCalc,
        statutoryConfigJson,
        effectiveMonth,
        branchesList,
      });

      const amountsByName = {};
      const addList = (list) => {
        (list || []).forEach((row) => {
          const name = String(row?.name || "").trim();
          if (!name) return;
          amountsByName[name] = Number(row.amount) || 0;
        });
      };
      addList(breakdown.earningsList);
      addList(breakdown.reimbursementsList);
      addList(breakdown.benefitsList);
      addList(breakdown.employerList);
      addList(breakdown.statutoryEmployerList);
      addList(breakdown.deductionsList);
      addList(breakdown.statutoryDeductionsList);

      map.set(id, {
        structureName: breakdown.structureName || structure.name || "—",
        structureId: structure.id ?? null,
        amountsByName,
        monthlyCtc: breakdown.monthlyCtc ?? Math.round(annual / 12),
        grossPay:
          breakdown.totals?.gross != null ? Number(breakdown.totals.gross) : null,
        netPay:
          breakdown.totals?.net != null ? Number(breakdown.totals.net) : null,
        componentNames: collectNames([
          breakdown.earningsList,
          breakdown.reimbursementsList,
          breakdown.statutoryEmployerList,
          breakdown.statutoryDeductionsList,
        ]),
      });
    } catch (err) {
      console.warn("Payroll breakdown enrichment failed", employee?.empCode, err);
      map.set(id, {
        structureName: structure.name || "—",
        structureId: structure.id ?? null,
        amountsByName: {},
        monthlyCtc: Math.round(annual / 12),
        grossPay: null,
        netPay: null,
      });
    }
  });

  return map;
}

export function templatesToStructures(templates) {
  return (Array.isArray(templates) ? templates : [])
    .map(templateToStructure)
    .filter(Boolean);
}

export function formatPayrollAmount(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en-IN");
}
